// src/api/coordinates.ts
import { api } from "./client";

export type GeoPermissionState = "granted" | "denied" | "prompt" | "unknown";

const GEO_KEY_LAST = "last_geo_data";
const GEO_KEY_TIMESTAMP = "last_geo_update";
const GEO_KEY_ALLOWED = "geo_allowed";

const DEBOUNCE_INTERVAL = 15 * 60 * 1000; // 15 минут
const DISTANCE_THRESHOLD_KM = 10;

let GEO_IN_PROGRESS = false;

// ------------------- Вспомогательные функции -------------------

function supportsPermissionsAPI(): boolean {
    return typeof navigator !== "undefined" && !!(navigator as any).permissions?.query;
}

function haversine(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // радиус Земли в км
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1 * Math.PI / 180) *
        Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function isDesktopChrome(): boolean {
    return (
        /Chrome/.test(navigator.userAgent) &&
        !/Android/.test(navigator.userAgent) &&
        !/Mobile/.test(navigator.userAgent)
    );
}

// ------------------- Проверка разрешений -------------------

export async function getGeoPermissionState(): Promise<GeoPermissionState> {
    try {
        if (!supportsPermissionsAPI()) return "unknown";
        const res = await (navigator as any).permissions.query({ name: "geolocation" as PermissionName });
        return res.state as GeoPermissionState;
    } catch {
        return "unknown";
    }
}

// ------------------- Геокодинг и API -------------------

export async function reverseGeocode(lat: number, lon: number): Promise<string | null> {
    try {
        const url = new URL("https://nominatim.openstreetmap.org/reverse");
        url.searchParams.set("format", "jsonv2");
        url.searchParams.set("lat", lat.toString());
        url.searchParams.set("lon", lon.toString());
        url.searchParams.set("zoom", "10");
        url.searchParams.set("accept-language", "en");

        const resp = await fetch(url.toString(), {
            headers: { "Accept": "application/json", "User-Agent": "TinderOne/1.0" },
        });

        if (!resp.ok) return null;
        const data = await resp.json();
        const a = data?.address || {};
        const parts = [a.city || a.town || a.village, a.state, a.country].filter(Boolean);
        return parts.join(", ") || null;
    } catch {
        return null;
    }
}

export async function postCoordinates(lat: number, long: number, formatted_address: string) {
    const token = localStorage.getItem("access_token");
    if (!token) return console.warn("[geo] skip post — no token");

    try {
        const response = await api.post("/users/coordinates", { lat, long, formatted_address });
        localStorage.setItem(GEO_KEY_LAST, JSON.stringify({ lat, long, formatted_address }));
        localStorage.setItem(GEO_KEY_TIMESTAMP, String(Date.now()));
        console.log("[geo] координаты успешно отправлены:", { lat, long, formatted_address });
        return response.data;
    } catch (error) {
        console.error("[geo] ошибка отправки:", error);
        throw error;
    }
}

export type LocationPayload = {
    lat: number;
    long: number;
    formatted_address?: string | null;
};

export async function postManualLocation(payload: LocationPayload) {
    const response = await api.post("/users/location/manual", {
        lat: payload.lat,
        long: payload.long,
        formatted_address: payload.formatted_address || null,
    });

    localStorage.setItem(GEO_KEY_LAST, JSON.stringify(payload));
    localStorage.setItem(GEO_KEY_TIMESTAMP, String(Date.now()));

    return response.data;
}

export async function postDeviceLocation(payload: LocationPayload) {
    const response = await api.post("/users/location/device", {
        lat: payload.lat,
        long: payload.long,
        formatted_address: payload.formatted_address || null,
    });

    localStorage.setItem(GEO_KEY_LAST, JSON.stringify(payload));
    localStorage.setItem(GEO_KEY_TIMESTAMP, String(Date.now()));
    markGeoAllowed();

    return response.data;
}

// ------------------- Получение координат -------------------

export type RequestCoordsResult =
    | { ok: true; lat: number; long: number; formatted_address: string }
    | { ok: false; reason: string; error?: any };

export async function getBrowserCoordinates(timeoutMs = 10000): Promise<RequestCoordsResult> {
    if (!("geolocation" in navigator)) return { ok: false, reason: "unsupported" };

    return new Promise((resolve) => {
        const onSuccess = (pos: GeolocationPosition) => {
            const lat = parseFloat(pos.coords.latitude.toFixed(6));
            const long = parseFloat(pos.coords.longitude.toFixed(6));
            resolve({ ok: true, lat, long, formatted_address: "" });
        };
        const onError = (err: GeolocationPositionError) => {
            const reasons: Record<number, string> = { 1: "denied", 2: "unavailable", 3: "timeout" };
            resolve({ ok: false, reason: reasons[err.code] || "error", error: err });
        };
        navigator.geolocation.getCurrentPosition(onSuccess, onError, {
            enableHighAccuracy: !isDesktopChrome(), 
            timeout: isDesktopChrome() ? 5000 : timeoutMs,
            maximumAge: isDesktopChrome() ? 60_000 : 0,
        });
    });
}

// ------------------- Основная логика -------------------

export async function requestAndSendCoordinates(force = false): Promise<boolean> {
    if (GEO_IN_PROGRESS) {
        console.warn("[geo] запрос уже в процессе — пропускаем повторный вызов");
        return false;
    }

    GEO_IN_PROGRESS = true;
    try {
        const permission = await getGeoPermissionState();
        if (location.hostname === "localhost" || location.hostname === "127.0.0.1") {
            console.warn("[geo] localhost detected — игнорируем permissions API");
        }

        if (permission === "denied") {
            console.warn("[geo] доступ к гео запрещён (permissions API)");
            const err: any = new Error("permission_denied");
            err.code = "permission_denied";
            throw err;
        }

        if (permission === "prompt" && !force && location.hostname !== "localhost") {
            console.warn("[geo] permission = prompt, авто-запрос не делаем (нужен явный клик пользователя)");
            const err: any = new Error("permission_prompt");
            err.code = "permission_prompt";
            throw err;
        }

        const coords = await getBrowserCoordinates(10000);
        if (!coords.ok) {
            throw coords;
        }

        const lastSync = Number(localStorage.getItem(GEO_KEY_TIMESTAMP) || 0);
        const now = Date.now();
        const lastData = localStorage.getItem(GEO_KEY_LAST);
        if (lastData) {
            const prev = JSON.parse(lastData);
            const distance = haversine(prev.lat, prev.long, coords.lat, coords.long);
            if (distance < DISTANCE_THRESHOLD_KM && !force) {
                console.log(`[geo] координаты не изменились (Δ=${distance.toFixed(1)} км)`);
                markGeoSyncedNow();
                return false;
            }
        }

        if (!force && !lastData && now - lastSync < DEBOUNCE_INTERVAL) {
            console.log("[geo] debounce: coordinates were recently synced");
            return false;
        }

        const formatted =
            (await reverseGeocode(coords.lat, coords.long)) ||
            `${coords.lat}, ${coords.long}`;

        await postCoordinates(coords.lat, coords.long, formatted);
        markGeoAllowed();
        markGeoSyncedNow();
        return true;
    } finally {
        GEO_IN_PROGRESS = false;
    }
}
// ------------------- Метки -------------------

export function markGeoAllowed(): void {
    localStorage.setItem(GEO_KEY_ALLOWED, "true");
}

export function isGeoAllowed(): boolean {
    return localStorage.getItem(GEO_KEY_ALLOWED) === "true";
}

export function markGeoSyncedNow(): void {
    localStorage.setItem(GEO_KEY_TIMESTAMP, String(Date.now()));
}

export function clearGeoSyncCache(): void {
    localStorage.removeItem(GEO_KEY_LAST);
    localStorage.removeItem(GEO_KEY_TIMESTAMP);
}
